
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert('すべての項目を入力してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('メッセージは500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch('/sendmail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      const result = await response.json().catch(() => ({ ok: false, message: '' }));

      if (response.ok && result.ok !== false) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage(typeof result.message === 'string' ? result.message : '');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('送信に失敗しました。しばらく時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-purple-900 via-blue-900 to-indigo-900 py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-5xl font-bold text-white mb-6">お問い合わせ</h1>
            <p className="text-xl text-purple-200 max-w-2xl mx-auto">
              ご質問、ご相談、お仕事のご依頼など、お気軽にお問い合わせください。
              できる限り迅速にご返信いたします。
            </p>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">お問い合わせ先</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-mail-line text-purple-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">メール</h3>
                      <p className="text-gray-600">info@kushita-akimasa.com</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-phone-line text-purple-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">電話</h3>
                      <p className="text-gray-600">03-1234-5678</p>
                      <p className="text-sm text-gray-500">平日 10:00-18:00</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-map-pin-line text-purple-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">住所</h3>
                      <p className="text-gray-600">
                        〒150-0001<br />
                        東京都渋谷区神宮前1-2-3<br />
                        ミュージックビル 5F
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-12">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">SNSでもお気軽に</h3>
                  <div className="flex space-x-4">
                    <a href="#" className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors cursor-pointer">
                      <i className="ri-twitter-fill text-xl"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors cursor-pointer">
                      <i className="ri-instagram-fill text-xl"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors cursor-pointer">
                      <i className="ri-youtube-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">メッセージを送る</h2>
                
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="山田太郎"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      件名 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">件名を選択してください</option>
                      <option value="ライブ・イベントについて">ライブ・イベントについて</option>
                      <option value="楽曲・アルバムについて">楽曲・アルバムについて</option>
                      <option value="メディア・取材について">メディア・取材について</option>
                      <option value="コラボレーション・お仕事依頼">コラボレーション・お仕事依頼</option>
                      <option value="ファンクラブについて">ファンクラブについて</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      メッセージ <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-vertical"
                      placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {formData.message.length}/500文字
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-check-circle-fill text-green-500 text-xl mr-3"></i>
                        <p className="text-green-800">メッセージを送信いたしました。ご返信までしばらくお待ちください。</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-error-warning-fill text-red-500 text-xl mr-3"></i>
                        <p className="text-red-800">{errorMessage || '送信に失敗しました。しばらく時間をおいて再度お試しください。'}</p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        送信中...
                      </span>
                    ) : (
                      '送信する'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">よくあるご質問</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3">Q. ライブのチケットはどこで購入できますか？</h3>
                <p className="text-gray-600">A. 各ライブ会場の公式サイト、またはチケットぴあ、ローソンチケットなどの主要チケット販売サイトでご購入いただけます。詳細は各ライブページをご確認ください。</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3">Q. ファンクラブの入会方法を教えてください。</h3>
                <p className="text-gray-600">A. 公式サイトのファンクラブページから入会手続きができます。年会費は3,000円で、限定コンテンツやチケット先行予約などの特典があります。</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3">Q. メディア取材の依頼はどちらにすればよいですか？</h3>
                <p className="text-gray-600">A. メディア関係者の方は、上記のお問い合わせフォームで「メディア・取材について」を選択してご連絡ください。担当者より折り返しご連絡いたします。</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3">Q. 楽曲の使用許可について教えてください。</h3>
                <p className="text-gray-600">A. 楽曲の商用利用や二次使用については、事前に許可が必要です。お問い合わせフォームより詳細をお聞かせください。</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;